<template>
  <lb-dialog
    v-model="localAddEditRuDialog"
    heading="Update Access details"
    width="800"
    :loading="addEditRuLoading"
    data-testid="access-management__update-dialog"
  >
    <template v-slot:body>
      <div v-if="localAddEditRuDialog && addEditRuLoading" class="d-flex flex-column gap-4" data-testid="access-management__update-skeleton">
        <v-skeleton-loader type="heading" width="40%" class="mb-1"></v-skeleton-loader>
        <div class="d-flex gap-4 flex-wrap">
          <v-skeleton-loader type="chip" width="150"></v-skeleton-loader>
          <v-skeleton-loader type="chip" width="150"></v-skeleton-loader>
          <v-skeleton-loader type="chip" width="150"></v-skeleton-loader>
        </div>
        <div class="d-flex flex-column gap-3">
          <v-card v-for="i in 4" :key="i" class="d-flex flex-column pa-4 usercard gap-3 shadow-off border-on">
            <v-skeleton-loader type="text" width="150" class="mb-4"></v-skeleton-loader>
            <div class="d-flex flex-wrap gap-3">
              <v-skeleton-loader type="chip" width="200"></v-skeleton-loader>
              <v-skeleton-loader type="chip" width="200"></v-skeleton-loader>
            </div>
          </v-card>
        </div>
      </div>
      <div v-else class="d-flex flex-column gap-4" data-testid="access-management__update-content">
        <template v-if="!bulkSubmit">
          <h4 class="font-size-md mb-1 noTransform" data-testid="access-management__update-title">
            {{ addEditRuData.code }} - {{ addEditRuData.name }}
          </h4>
          <div class="d-flex gap-4 flex-wrap">
            <v-chip label class="chip-choose" data-testid="access-management__network-chip">
              <span class="categorystype">{{ $nova.labelsName('access_management','division_label','Network') }} - {{ addEditRuData.network }}</span>
            </v-chip>
            <div class="vertical"></div>
            <v-chip label class="chip-choose" data-testid="access-management__region-chip">
              <span class="categorystype">{{ $nova.labelsName('access_management','region_label','Region') }} - {{ addEditRuData.region }}</span>
            </v-chip>
            <div class="vertical"></div>
            <v-chip label class="chip-choose" data-testid="access-management__country-chip">
              <span class="categorystype">{{ $nova.labelsName('access_management','country_label','Country') }} - {{ addEditRuData.country }}</span>
            </v-chip>
          </div>
        </template>
        <template v-else>
          <div class="d-flex align-center" data-testid="access-management__bulk-summary">
            <CountSection :count="addEditRuData._id && addEditRuData._id.length" class="gap-120"
            :networkLabel="$nova.labelsName('access_management','ru_entity_plural_label','Reporting Units')"
            :countLabel="$nova.labelsName('access_management','commentary_count_label','Selected')" />
          </div>
        </template>

        <v-alert
          v-if="addEditRuData.__sodMixed"
          type="error"
          dense
          class="mb-0"
          border="left"
          data-testid="access-management__sod-warning"
        >
          {{ mixedSodWarningMessage }}
        </v-alert>

        <div class="d-flex flex-column gap-3">
            <!-- Local Preparer -->
          <v-card class="d-flex flex-column pa-4 usercard gap-3 shadow-off border-on" data-testid="access-management__local-preparer-card">
            <div class="d-flex align-center justify-space-between">
              <h5 class="font-size-md mb-0">{{ $nova.labelsName('access_management','commentary_local_preparer_label','Local Preparer') }}</h5>
              <v-menu v-model="menuOpen.submitter" :close-on-content-click="false" offset-y left>
                <template v-slot:activator="{ on: menuOn, attrs }">
                  <v-btn
                    outlined
                    class="pa-2 userAddBtn"
                    @click="openUserMenu('submitter')"
                    v-on="menuOn"
                    v-bind="attrs"
                    data-testid="access-management__add-preparer-button"
                  >
                    <v-icon>mdi-account-plus</v-icon>
                    <span>Add User</span>
                  </v-btn>
                </template>
                <v-card class="add-user-menu" min-width="280">
                  <v-card-title class="add-user-menu__header popupHeader pa-3 d-flex align-center justify-space-between">
                    <span class="font-weight-medium">Add Users</span>
                    <v-btn icon @click="menuOpen.submitter = false" class="white--text">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-3">
                    <lb-dropdown
                      hidedetails
                      v-model="localAddUserValue"
                      :items="filteredUserList"
                      itemvalue="_id"
                      itemtext="newdisplay"
                      label="Select User"
                      item-class="noTransform"
                    />
                    <div class="d-flex justify-end mt-3">
                      <v-btn color="primary" :disabled="!localAddUserValue" @click="selectUser('submitter')"><span>Apply</span></v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            <div class="d-flex flex-wrap gap-3" data-testid="access-management__local-preparer-list">
              <template v-if="(addEditRuData.submitter || []).length > 0">
                <v-chip
                  v-for="(v, k) in addEditRuData.submitter"
                  :key="k"
                  class="email-chip pr-0"
                  data-testid="access-management__local-preparer-chip"
                >
                  <span class="textLowercase">{{ v.name }}</span>
                  <v-btn
                    icon
                    class="ml-1"
                    @click="$emit('remove-access', k, 'submitter')"
                    data-testid="access-management__local-preparer-remove-button"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </v-chip>
              </template>
              <template v-else>
                <div class="grey--text px-2" data-testid="access-management__local-preparer-empty">No user selected</div>
              </template>
            </div>
          </v-card>

          <!-- Local Approver -->
          <v-card class="d-flex flex-column pa-4 usercard gap-3 shadow-off border-on" data-testid="access-management__local-approver-card">
            <div class="d-flex align-center justify-space-between">
              <h5 class="font-size-md mb-0">{{ $nova.labelsName('access_management','commentary_local_approver_label','Local Approver') }}</h5>
              <v-menu v-model="menuOpen.approver" :close-on-content-click="false" offset-y left>
                <template v-slot:activator="{ on: menuOn, attrs }">
                  <v-btn
                    outlined
                    class="pa-2 userAddBtn"
                    @click="openUserMenu('approver')"
                    v-on="menuOn"
                    v-bind="attrs"
                    data-testid="access-management__add-approver-button"
                  >
                    <v-icon>mdi-account-plus</v-icon>
                    <span>Add User</span>
                  </v-btn>
                </template>
                <v-card class="add-user-menu" min-width="280">
                  <v-card-title class="add-user-menu__header popupHeader pa-3 d-flex align-center justify-space-between">
                    <span class="font-weight-medium">Add Users</span>
                    <v-btn icon @click="menuOpen.approver = false" class="white--text">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-3">
                    <lb-dropdown
                      hidedetails
                      v-model="localAddUserValue"
                      :items="filteredUserList"
                      itemvalue="_id"
                      itemtext="newdisplay"
                      label="Select User"
                      item-class="noTransform"
                    />
                    <div class="d-flex justify-end mt-3">
                      <v-btn color="primary" :disabled="!localAddUserValue" @click="selectUser('approver')"><span>Apply</span></v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            <div class="d-flex flex-wrap gap-3" data-testid="access-management__local-approver-list">
              <template v-if="(addEditRuData.approver || []).length > 0">
                <v-chip
                  v-for="(v, k) in addEditRuData.approver"
                  :key="k"
                  class="email-chip pr-0"
                  data-testid="access-management__local-approver-chip"
                >
                  <span class="textLowercase">{{ v.name }}</span>
                  <v-btn
                    icon
                    class="ml-1"
                    @click="$emit('remove-access', k, 'approver')"
                    data-testid="access-management__local-approver-remove-button"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </v-chip>
              </template>
              <template v-else>
                <div class="grey--text px-2" data-testid="access-management__local-approver-empty">No user selected</div>
              </template>
            </div>
          </v-card>

          <!-- Regional Approver -->
          <v-card class="d-flex flex-column pa-4 usercard gap-3 shadow-off border-on" data-testid="access-management__regional-approver-card">
            <div class="d-flex align-center justify-space-between">
              <h5 class="font-size-md mb-0">{{ $nova.labelsName('access_management','commentary_regional_approver_label','Regional Approver') }}</h5>
              <v-menu v-model="menuOpen.regional_approver" :close-on-content-click="false" offset-y left>
                <template v-slot:activator="{ on: menuOn, attrs }">
                  <v-btn
                    outlined
                    class="pa-2 userAddBtn"
                    @click="openUserMenu('regional_approver')"
                    v-on="menuOn"
                    v-bind="attrs"
                    data-testid="access-management__add-regional-approver-button"
                  >
                    <v-icon>mdi-account-plus</v-icon>
                    <span>Add User</span>
                  </v-btn>
                </template>
                <v-card class="add-user-menu" min-width="280">
                  <v-card-title class="add-user-menu__header popupHeader pa-3 d-flex align-center justify-space-between">
                    <span class="font-weight-medium">Add Users</span>
                    <v-btn icon @click="menuOpen.regional_approver = false" class="white--text">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-3">
                    <lb-dropdown
                      hidedetails
                      v-model="localAddUserValue"
                      :items="filteredUserList"
                      itemvalue="_id"
                      itemtext="newdisplay"
                      label="Select User"
                      item-class="noTransform"
                    />
                    <div class="d-flex justify-end mt-3">
                      <v-btn color="primary" :disabled="!localAddUserValue" @click="selectUser('regional_approver')"><span>Apply</span></v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            <div class="d-flex flex-wrap gap-3" data-testid="access-management__regional-approver-list">
              <template v-if="(addEditRuData.regional_approver || []).length > 0">
                <v-chip
                  v-for="(v, k) in addEditRuData.regional_approver"
                  :key="k"
                  class="email-chip pr-0"
                  data-testid="access-management__regional-approver-chip"
                >
                  <span class="textLowercase">{{ v.name }}</span>
                  <v-btn
                    icon
                    class="ml-1"
                    color="error--text"
                    @click="$emit('remove-access', k, 'regional_approver')"
                    data-testid="access-management__regional-approver-remove-button"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </v-chip>
              </template>
              <template v-else>
                <div class="grey--text px-2" data-testid="access-management__regional-approver-empty">No user selected</div>
              </template>
            </div>
          </v-card>

          <!-- Manager -->
          <v-card class="d-flex flex-column pa-4 usercard gap-3 shadow-off border-on" data-testid="access-management__manager-card">
            <div class="d-flex align-center justify-space-between">
              <h5 class="font-size-md mb-0">Manager</h5>
              <v-menu v-model="menuOpen.manager" :close-on-content-click="false" offset-y left>
                <template v-slot:activator="{ on: menuOn, attrs }">
                  <v-btn
                    outlined
                    class="pa-2 userAddBtn"
                    @click="openUserMenu('manager')"
                    v-on="menuOn"
                    v-bind="attrs"
                    data-testid="access-management__add-manager-button"
                  >
                    <v-icon>mdi-account-plus</v-icon>
                    <span>Add User</span>
                  </v-btn>
                </template>
                <v-card class="add-user-menu" min-width="280">
                  <v-card-title class="add-user-menu__header popupHeader pa-3 d-flex align-center justify-space-between">
                    <span class="font-weight-medium">Add Users</span>
                    <v-btn icon @click="menuOpen.manager = false" class="white--text">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pa-3">
                    <lb-dropdown
                      hidedetails
                      v-model="localAddUserValue"
                      :items="filteredUserList"
                      itemvalue="_id"
                      itemtext="newdisplay"
                      label="Select User"
                      item-class="noTransform"
                    />
                    <div class="d-flex justify-end mt-3">
                      <v-btn color="primary" :disabled="!localAddUserValue" @click="selectUser('manager')"><span>Apply</span></v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
            <div class="d-flex flex-wrap gap-3" data-testid="access-management__manager-list">
              <template v-if="(addEditRuData.manager || []).length > 0">
                <v-chip
                  v-for="(v, k) in addEditRuData.manager"
                  :key="k"
                  class="email-chip pr-0"
                  data-testid="access-management__manager-chip"
                >
                  <span class="textLowercase">{{ v.name }}</span>
                  <v-btn
                    icon
                    class="ml-1"
                    @click="$emit('remove-access', k, 'manager')"
                    data-testid="access-management__manager-remove-button"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </v-chip>
              </template>
              <template v-else>
                <div class="grey--text px-2" data-testid="access-management__manager-empty">No user selected</div>
              </template>
            </div>
          </v-card>
        </div>

        <lb-divider class="mt-3 mb-2"></lb-divider>

        <div class="d-flex flex-column gap-3" data-testid="access-management__change-summary">
          <div
            class="font-size-md text-center"
            v-if="hasNoChanges"
            data-testid="access-management__change-summary-empty"
          >
            No changes performed/ No alterations performed
          </div>
          <template v-else>
            <div class="d-flex align-center">
              <div class="font-size-md">Changes</div>
              <v-spacer></v-spacer>
              <v-btn class="border-left-error-btn border-on" data-testid="access-management__change-removed-label">
                <span>Removed</span>
              </v-btn>
              <v-btn class="ml-2 border-left-success-btn border-on" data-testid="access-management__change-added-label">
                <span>Added</span>
              </v-btn>
            </div>

              <!-- Helper component for change list could be extracted, but keeping inline for now -->
            <div v-if="(userChanges.submitter || []).length > 0" data-testid="access-management__change-preparer" class="d-flex flex-column gap-2">
              <div class="font-weight-medium">
                {{ $nova.labelsName('access_management','commentary_local_preparer_label','Local Preparer') }}:
              </div>
              <div class="d-flex flex-wrap gap-2">
                <v-chip
                  v-for="(v, k) in (userChanges.submitter || [])"
                  :key="`change-submit-${k}`"
                  :class="`mr-1 mb-1 pr-0 ${v.action==='remove'?'border-left-error-btn':'border-left-success-btn'}`"
                  data-testid="access-management__change-preparer-chip"
                >
                  <span class="textLowercase">{{ v.user.name }}</span>
                  <v-btn
                    icon
                    class="ml-1"
                    @click="$emit('undo-change', v.user?._id, k, 'submitter', v.action)"
                    data-testid="access-management__change-preparer-undo-button"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </v-chip>
              </div>
            </div>
            
            <div v-if="(userChanges.approver || []).length > 0" data-testid="access-management__change-approver" class="d-flex flex-column gap-2">
                <div class="font-weight-medium">
                  {{ $nova.labelsName('access_management','commentary_local_approver_label','Local Approver') }}:
                </div>
                <div class="d-flex flex-wrap gap-2">
                  <v-chip
                    v-for="(v, k) in (userChanges.approver || [])"
                    :key="`change-approver-${k}`"
                    :class="`mr-1 mb-1 pr-0 ${v.action==='remove'?'border-left-error-btn':'border-left-success-btn'}`"
                    data-testid="access-management__change-approver-chip"
                  >
                    <span class="textLowercase">{{ v.user.name }}</span>
                    <v-btn
                      icon
                      class="ml-1"
                      @click="$emit('undo-change', v.user?._id, k, 'approver', v.action)"
                      data-testid="access-management__change-approver-undo-button"
                    >
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>
                  </v-chip>
                </div>
            </div>

            <div v-if="(userChanges.regional_approver || []).length > 0" data-testid="access-management__change-regional-approver" class="d-flex flex-column gap-2">
              <div class="font-weight-medium">
                {{ $nova.labelsName('access_management','commentary_regional_approver_label','Regional Approver') }}:
              </div>
              <div class="d-flex flex-wrap gap-2">
                <v-chip
                  v-for="(v, k) in (userChanges.regional_approver || [])"
                  :key="`change-regional-${k}`"
                  :class="`mr-1 mb-1 pr-0 ${v.action==='remove'?'border-left-error-btn':'border-left-success-btn'}`"
                  data-testid="access-management__change-regional-approver-chip"
                >
                  <span class="textLowercase">{{ v.user.name }}</span>
                  <v-btn
                    icon
                    class="ml-1"
                    @click="$emit('undo-change', v.user?._id, k, 'regional_approver', v.action)"
                    data-testid="access-management__change-regional-approver-undo-button"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </v-chip>
              </div>
            </div>

            <div v-if="(userChanges.manager || []).length > 0" data-testid="access-management__change-manager" class="d-flex flex-column gap-2">
              <div class="font-weight-medium">Manager(s):</div>
              <div class="d-flex flex-wrap gap-2">
                <v-chip
                  v-for="(v, k) in (userChanges.manager || [])"
                  :key="`change-manager-${k}`"
                  :class="`mr-1 mb-1 pr-0 ${v.action==='remove'?'border-left-error-btn':'border-left-success-btn'}`"
                  data-testid="access-management__change-manager-chip"
                >
                  <span class="textLowercase">{{ v.user.name }}</span>
                  <v-btn
                    icon
                    class="ml-1"
                    @click="$emit('undo-change', v.user?._id, k, 'manager', v.action)"
                    data-testid="access-management__change-manager-undo-button"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </v-chip>
              </div>
            </div>

          </template>
        </div>

        <lb-divider class="mt-3 mb-2" v-if="(ruUpdateErrorData || []).length > 0"></lb-divider>

        <div class="d-flex flex-column gap-2" v-if="(ruUpdateErrorData || []).length > 0" data-testid="access-management__invalid-changes">
          <div class="font-size-md">Invalid Changes</div>
          <div class="d-flex flex-wrap gap-2">
            <div
              v-for="(invalid, invalidIndex) in (ruUpdateErrorData || [])"
              :key="`invalid-${invalidIndex}`"
              class="d-flex flex-column gap-1"
            >
              <v-chip class="mr-0 mb-1 pr-3 border-left-error-btn fit-content" data-testid="access-management__invalid-ru-chip">
                <span>{{ ruIdCodeMap[invalid.rucode] }}</span>
              </v-chip>
              <v-chip
                v-for="(userid, userIndex) in (invalid.userids || [])"
                :key="`invalid-${invalidIndex}-${userIndex}`"
                class="mr-0 mb-1 pr-3 border-left-error-btn fit-content"
                data-testid="access-management__invalid-user-chip"
              >
                <span>{{ (userList[userid] && userList[userid].email) || '' }}</span>
              </v-chip>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        v-if="!hasNoChanges"
        @click="$emit('update-user-access')"
        data-testid="access-management__update-button"
      >
        <span>Update</span>
      </v-btn>
    </template>
  </lb-dialog>
</template>

<script>
import CountSection from '../../GlSelection/CountSection.vue';

export default {
    name: 'AccessUpdateDialog',
    components: {
        CountSection
    },
    props: {
        value: { type: Boolean, default: false },
        addEditRuLoading: { type: Boolean, default: false },
        bulkSubmit: { type: Boolean, default: false },
        addEditRuData: { type: Object, default: () => ({}) },
        mixedSodWarningMessage: { type: String, default: '' },
        userChanges: { type: Object, default: () => ({}) },
        ruUpdateErrorData: { type: Array, default: () => [] },
        ruIdCodeMap: { type: Object, default: () => ({}) },
        userList: { type: Object, default: () => ({}) },
        filteredUserList: { type: Array, default: () => [] },
        addUserValue: { type: String, default: '' },
    },
    data() {
        return {
            menuOpen: {
                submitter: false,
                approver: false,
                regional_approver: false,
                manager: false,
            },
        };
    },
    computed: {
        localAddEditRuDialog: {
            get() { return this.value; },
            set(val) { this.$emit('input', val); }
        },
        hasNoChanges() {
            const { submitter, approver, regional_approver, manager } = this.userChanges;
            return [...(submitter || []), ...(approver || []), ...(regional_approver || []), ...(manager || [])].length === 0;
        },
        localAddUserValue: {
            get() { return this.addUserValue; },
            set(val) { this.$emit('update:addUserValue', val); }
        },
    },
    methods: {
        openUserMenu(type) {
            this.$emit('update:addUsertype', type);
            this.$emit('update:addUserValue', '');
            this.menuOpen[type] = true;
        },
        selectUser(type) {
            this.$emit('add-user');
            this.menuOpen[type] = false;
        },
    }
}
</script>

<style lang="scss" scoped>
.border-left-success-btn {
  border: 1px solid var(--color-success) !important;
  background: none !important;
}

.border-left-error-btn {
  border: 1px solid var(--color-error) !important;
  background: none !important;
}

.userAddBtn .v-btn__content {
  gap: 8px;
}
.fit-content{
  width: fit-content;
}
</style>